import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '@store/index';
import { setChatWidth } from '@features/ui/uiSlice';
import { CHAT_PANEL } from '@constants/layout';

const clamp = (value: number) =>
  Math.min(CHAT_PANEL.MAX_WIDTH, Math.max(CHAT_PANEL.MIN_WIDTH, value));

interface ChatResize {
  /** Live width during a drag; falls back to the committed width at rest. */
  width: number;
  isResizing: boolean;
  handleProps: {
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  };
}

/**
 * The in-flight width is local; the store is written once on release. Per-move
 * dispatch wrote to localStorage and re-rendered the whole shell ~120x/second.
 */
export function useChatResize(committedWidth: number): ChatResize {
  const dispatch = useAppDispatch();
  const [draftWidth, setDraftWidth] = useState<number | null>(null);
  const origin = useRef({ x: 0, width: 0 });

  const clearCursor = useCallback(() => {
    document.body.style.removeProperty('cursor');
    document.body.style.removeProperty('user-select');
  }, []);

  useEffect(() => clearCursor, [clearCursor]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      origin.current = { x: event.clientX, width: committedWidth };
      setDraftWidth(committedWidth);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [committedWidth],
  );

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    setDraftWidth(clamp(origin.current.width + (origin.current.x - event.clientX)));
  }, []);

  // pointerup and pointercancel are mutually exclusive; both must release.
  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      clearCursor();

      // Read the draft here rather than inside a setDraftWidth updater. React
      // runs updaters during the render phase, so dispatching from one is a
      // store write during render — it logs "Cannot update a component while
      // rendering a different component", and StrictMode double-invokes
      // updaters, so the width was dispatched twice per drag.
      if (draftWidth !== null && draftWidth !== committedWidth) {
        dispatch(setChatWidth(draftWidth));
      }
      setDraftWidth(null);
    },
    [clearCursor, dispatch, committedWidth, draftWidth],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? CHAT_PANEL.KEYBOARD_STEP_LARGE : CHAT_PANEL.KEYBOARD_STEP;
      const delta = event.key === 'ArrowLeft' ? step : event.key === 'ArrowRight' ? -step : 0;
      if (delta === 0) return;

      event.preventDefault();
      dispatch(setChatWidth(clamp(committedWidth + delta)));
    },
    [dispatch, committedWidth],
  );

  return {
    width: draftWidth ?? committedWidth,
    isResizing: draftWidth !== null,
    handleProps: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag, onKeyDown },
  };
}
